import { axiosInstance } from '../clients/axiosClient';
import { API_V1_PREFIX } from '../utils/constants';

// Types based on API documentation
export interface BodySystem {
  id: number;
  title: string;
}

export interface Biomarker {
  id: number;
  name: string;
}

export interface Panel {
  id: number;
  name: string;
  catalog_id: string;
  price: number;
  body_system_ids: number[];
  biomarker_ids: number[];
  created_at: string;
  updated_at: string;
}

export interface PanelWithDetails {
  id: number;
  name: string;
  catalog_id: string;
  price: number;
  non_discounted_price?: number;
  body_systems: BodySystem[];
  biomarkers: Biomarker[];
  created_at: string;
  updated_at: string;
}

export interface CreatePanelRequest {
  name: string;
  catalog_id: string;
  price: number;
  body_system_ids: number[];
  biomarker_ids: number[];
  non_discounted_price: number;
}

export interface UpdatePanelRequest {
  id: number;
  name: string;
  catalog_id: string;
  price: number;
  non_discounted_price: number;
  body_system_ids: number[];
  biomarker_ids: number[];
}

export interface DeletePanelRequest {
  panel_id: number;
}

export interface GetPanelByIdRequest {
  panel_id: number;
}

export interface PanelListResponse {
  panels: PanelWithDetails[];
  total_count: number;
}

export interface CreatePanelResponse {
  id: number;
  status: string;
}

export interface UpdatePanelResponse {
  id: number;
  status: string;
}

export interface DeletePanelResponse {
  status: string;
  message?: string;
}

export interface GetPanelByIdResponse {
  id: number;
  name: string;
  catalog_id: string;
  price: number;
  biomarkers: Biomarker[];
  body_systems: BodySystem[];
  created_at: string;
  updated_at: string;
  status: string;
}

export interface ErrorResponse {
  description: string;
}

// API Functions

/**
 * Get list of panels with pagination, search, and filters
 * GET /api/v1/panels/list
 */
export const getPanelsList = async (
  search_query?: string | null,
  limit: number = 10,
  offset: number = 0,
  sort_field?: string | null,
  sort_order: 'asc' | 'desc' = 'desc',
  biomarker_id?: number | null,
  body_system_id?: number | null
): Promise<PanelListResponse> => {
  try {
    const params = new URLSearchParams();
    
    if (search_query) {
      params.append('search_query', search_query);
    }
    
    if (sort_field) {
      params.append('sort_field', sort_field);
    }
    
    params.append('sort_order', sort_order);
    params.append('limit', limit.toString());
    params.append('offset', offset.toString());
    
    if (biomarker_id !== null && biomarker_id !== undefined) {
      params.append('biomarker_id', biomarker_id.toString());
    }
    
    if (body_system_id !== null && body_system_id !== undefined) {
      params.append('body_system_id', body_system_id.toString());
    }

    const { data } = await axiosInstance.get(
      `${API_V1_PREFIX}panels/list?${params.toString()}`
    );

    return data;
  } catch (error: any) {
    throw new Error(error?.message || 'Failed to fetch panels list');
  }
};

/**
 * Get panel by ID
 * POST /api/v1/panels/get/
 */
export const getPanelById = async (
  panelId: number
): Promise<GetPanelByIdResponse> => {
  try {
    const { data } = await axiosInstance.post(
      `${API_V1_PREFIX}panels/get/`,
      {
        panel_id: panelId
      }
    );

    return data;
  } catch (error: any) {
    throw new Error(error?.message || 'Failed to fetch panel');
  }
};

/**
 * Create a new panel
 * POST /api/v1/panels/
 */
export const createPanel = async (
  panelData: CreatePanelRequest
): Promise<CreatePanelResponse> => {
  try {
    const { data } = await axiosInstance.post(
      `${API_V1_PREFIX}panels/`,
      panelData
    );

    return data;
  } catch (error: any) {
    throw new Error(error?.message || 'Failed to create panel');
  }
};

/**
 * Update an existing panel
 * PATCH /api/v1/panels/
 */
export const updatePanel = async (
  panelData: UpdatePanelRequest
): Promise<UpdatePanelResponse> => {
  try {
    const { data } = await axiosInstance.patch(
      `${API_V1_PREFIX}panels/`,
      panelData
    );

    return data;
  } catch (error: any) {
    throw new Error(error?.message || 'Failed to update panel');
  }
};

/**
 * Delete a panel by ID
 * DELETE /api/v1/panels/
 */
export const deletePanel = async (
  panelId: number
): Promise<DeletePanelResponse> => {
  try {
    const { data } = await axiosInstance.delete(
      `${API_V1_PREFIX}panels/`,
      {
        data: {
          panel_id: panelId
        }
      }
    );

    return data;
  } catch (error: any) {
    throw new Error(error?.message || 'Failed to delete panel');
  }
};

